import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Play } from "lucide-react";
import type { ReportInput, ReportOption } from "../../../api/portalReportsApi";
import { todayInTz, tzLabel } from "../ReportBits";

const inputStyle: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid var(--portal-border)",
  borderRadius: 6,
  fontFamily: "inherit",
  fontSize: "0.9rem",
  background: "white",
  minWidth: 160,
};

/**
 * Resolves "today", "today-7", "month" (first day of the current month),
 * "month+1" (first day of next month, an exclusive "to") and "now" defaults in
 * the report time zone; literals pass through. Mirrors ReportRunner.ResolveDefault.
 */
export function resolveDefault(input: ReportInput): string {
  const d = (input.default ?? "").trim();
  if (!d) return "";
  const low = d.toLowerCase();
  if (low === "now") {
    const today = todayInTz();
    const now = new Date();
    return `${today}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  }
  if (low.startsWith("today") || low.startsWith("month")) {
    const rest = low.slice(5).replace(/\s/g, "");
    const offset = rest && Number.isFinite(Number(rest)) ? Number(rest) : 0;
    const base = todayInTz();
    const [y, m, dd] = base.split("-").map(Number);
    const dt = low.startsWith("month") ? new Date(Date.UTC(y, m - 1 + offset, 1)) : new Date(Date.UTC(y, m - 1, dd + offset));
    const day = dt.toISOString().slice(0, 10);
    return input.type === "datetime" ? `${day}T00:00` : day;
  }
  return d;
}

export function initialValues(inputs: ReportInput[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const i of inputs) out[i.key] = resolveDefault(i);
  return out;
}

/**
 * The filter bar of a dynamic report: one control per input, a Go button.
 * `loadOptions` fetches options for selects backed by a procedure.
 */
export function ReportInputsForm({
  inputs,
  values,
  onChange,
  onSubmit,
  loading,
  loadOptions,
}: {
  inputs: ReportInput[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSubmit: () => void;
  loading: boolean;
  loadOptions?: (key: string) => Promise<ReportOption[]>;
}) {
  const [remoteOptions, setRemoteOptions] = useState<Record<string, ReportOption[]>>({});
  const remoteKeys = useMemo(() => inputs.filter((i) => i.type === "select" && i.optionsProcedure).map((i) => i.key), [inputs]);

  useEffect(() => {
    if (!loadOptions || remoteKeys.length === 0) return;
    let active = true;
    for (const key of remoteKeys) {
      loadOptions(key)
        .then((opts) => active && setRemoteOptions((o) => ({ ...o, [key]: opts })))
        .catch(() => active && setRemoteOptions((o) => ({ ...o, [key]: [] })));
    }
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteKeys.join("|")]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", alignItems: "flex-end", gap: 14, flexWrap: "wrap", fontSize: "0.9rem", margin: "4px 0 12px" }}>
      {inputs.map((input) => {
        const label = input.label || input.key;
        const value = values[input.key] ?? "";
        const set = (v: string) => onChange(input.key, v);
        const withTz = input.type === "date" || input.type === "datetime";
        const control = (() => {
          switch (input.type) {
            case "number":
              return <input type="number" className="portal-input-inline" style={inputStyle} value={value} onChange={(e) => set(e.target.value)} required={input.required} />;
            case "date":
              return <input type="date" className="portal-input-inline" style={inputStyle} value={value} onChange={(e) => set(e.target.value)} required={input.required} />;
            case "datetime":
              return <input type="datetime-local" className="portal-input-inline" style={inputStyle} value={value} onChange={(e) => set(e.target.value)} required={input.required} />;
            case "bool":
              return (
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingBottom: 8 }}>
                  <input type="checkbox" checked={value === "1" || value === "true"} onChange={(e) => set(e.target.checked ? "1" : "0")} />
                  {label}
                </label>
              );
            case "select": {
              const opts = input.optionsProcedure ? remoteOptions[input.key] ?? [] : input.options ?? [];
              return (
                <select className="portal-input-inline" style={inputStyle} value={value} onChange={(e) => set(e.target.value)} required={input.required}>
                  {!input.required && <option value="">— All —</option>}
                  {opts.map((o) => (
                    <option key={o.value} value={o.value}>{o.label || o.value}</option>
                  ))}
                </select>
              );
            }
            default:
              return <input type="text" className="portal-input-inline" style={inputStyle} value={value} onChange={(e) => set(e.target.value)} required={input.required} />;
          }
        })();
        if (input.type === "bool") return <span key={input.key}>{control}</span>;
        return (
          <label key={input.key} style={{ display: "grid", gap: 4 }} title={input.help || undefined}>
            <span style={{ color: "var(--portal-muted)" }}>
              {label}
              {withTz && input.localToUtc !== false ? ` (${tzLabel()})` : ""}
              {input.required ? " *" : ""}
            </span>
            {control}
          </label>
        );
      })}
      <button type="submit" className="btn btn-primary" disabled={loading} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Play size={14} /> {loading ? "Loading…" : "Go"}
      </button>
    </form>
  );
}
